import { TransformedAuditData } from '@dealstack/frontend/shared/components/Audit/types.ts';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { AuditAction } from '@dealstack/shared/lib/generated';
import { Text } from '@mantine/core';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	auditAction: AuditAction;
}

export const getAuditListColumns = ({
	tCapManage,
	auditAction,
}: Props): ExtendedColumnDef<TransformedAuditData>[] => {
	// Values are masked in session replay (a bank account's diff carries the full IBAN);
	// field names stay readable.
	if (auditAction === AuditAction.UPDATE) {
		return [
			createTextColumn({
				headerText: tCapManage('transactions.details.history.fields'),
				accessorKey: 'field',
				enableGlobalFilter: true,
				enableSorting: true,
			}),
			createTextColumn({
				headerText: tCapManage('transactions.details.history.before_update'),
				accessorKey: 'beforeValue',
				enableGlobalFilter: true,
				enableSorting: true,
				cell: ({ row }) => {
					const isChanged = row.original.isChanged;

					return (
						<Text
							size="B6Medium"
							c={isChanged ? defaultTheme.v3.colors.red[6] : undefined}
							className={REPLAY_MASK_CLASS}
							data-sentry-mask
						>
							• {String(row.getValue('beforeValue') ?? '')}
						</Text>
					);
				},
			}),
			createTextColumn({
				headerText: tCapManage('transactions.details.history.after_update'),
				accessorKey: 'afterValue',
				enableGlobalFilter: true,
				enableSorting: true,
				cell: ({ row }) => {
					const isChanged = row.original.isChanged;

					return (
						<Text
							size="B6Medium"
							c={isChanged ? defaultTheme.v3.colors.teal[9] : undefined}
							className={REPLAY_MASK_CLASS}
							data-sentry-mask
						>
							• {String(row.getValue('afterValue') ?? '')}
						</Text>
					);
				},
			}),
		];
	} else {
		const actionLabel =
			auditAction === AuditAction.CREATE
				? `${tCapManage('transactions.details.history.created')}`
				: `${tCapManage('transactions.details.history.deleted')}`;

		return [
			createTextColumn({
				headerText: tCapManage('transactions.details.history.fields'),
				accessorKey: 'field',
				enableGlobalFilter: true,
				enableSorting: true,
			}),
			createTextColumn({
				headerText: actionLabel,
				accessorKey: 'value',
				enableGlobalFilter: true,
				enableSorting: true,
				cell: ({ cell }) => (
					<Text size="B6Medium" className={REPLAY_MASK_CLASS} data-sentry-mask>
						• {cell.getValue<string>()}
					</Text>
				),
			}),
		];
	}
};
